// src/lib/ai-adapter.js: Supabase Edge Function (Mistral AI proxy).
//
// De functie bouwt de prompt server-side; de client stuurt alleen { task,
// payload }. localStorage-cache op SHA-256(task + payload).

async function aiCacheKey(task, payload) {
  const buf = new TextEncoder().encode(task + "\n" + JSON.stringify(payload));
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return "ai-cache:" + Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, "0")).join("");
}

// Pakt een leesbare melding uit een (mogelijk geneste) Mistral-foutbody.
// Edge function wraps de upstream-error als { error: "<JSON-string>" }.
function parseAiError(raw, fallback) {
  if (!raw) return fallback;
  if (typeof raw !== "string") raw = String(raw);
  try {
    const inner = JSON.parse(raw);
    const msg = inner?.error?.message || inner?.message;
    if (msg) return msg;
  } catch { /* niet geneste JSON, neem ruwe string */ }
  return raw;
}

export const claude = {
  // task: "suggestRol"; payload: taak-specifieke velden.
  async call(task, payload, opts = {}) {
    const key = await aiCacheKey(task, payload);
    const cached = localStorage.getItem(key);
    if (cached !== null) return cached;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    let res;
    try {
      // Pinned to Frankfurt, closest to Mistral (Paris). A query parameter, not the x-region
      // header, so the browser's CORS preflight stays as the function allows it.
      res = await fetch(`${supabaseUrl}/functions/v1/ai?forceFunctionRegion=eu-central-1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${anonKey}`,
          "apikey": anonKey,
        },
        body: JSON.stringify({ task, payload }),
        signal: opts.signal,
      });
    } catch (netErr) {
      if (netErr.name === "AbortError") throw netErr;
      const msg = `Netwerkfout: ${netErr.message || "geen verbinding"}`;
      window.__lastAiError = msg;
      throw new Error(msg);
    }

    if (!res.ok) {
      let detail;
      try {
        const body = await res.json();
        detail = parseAiError(
          body?.error ?? body?.message ?? body?.msg,
          `Server gaf ${res.status} terug`
        );
      } catch {
        detail = `Server gaf ${res.status} terug`;
      }
      window.__lastAiError = detail;
      throw new Error(detail);
    }

    const { text, error } = await res.json();
    if (error) {
      const msg = parseAiError(error, "Onbekende fout van de digitale assistent");
      window.__lastAiError = msg;
      throw new Error(msg);
    }

    try { localStorage.setItem(key, text); } catch { /* quota full → skip */ }
    return text;
  },
};
